import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * Binds a Zocdoc booking to the beHuman appointment that *is* the same visit, by writing
 * `ZOCDOC_APPOINTMENT_SYSTEM|<zocdoc appointment id>` onto `Appointment.identifier`.
 *
 * **Why the identifier and not a remembered match.** Until this is written, "these two are the
 * same visit" is inferred by comparing start instants — fine for offering a match to a human,
 * never enough to act on. Once written, a change arriving from Zocdoc that names only an
 * appointment id can find the beHuman appointment and do something about it: a Zocdoc
 * cancellation becoming a beHuman cancellation is that lookup and nothing more.
 *
 * **Nothing is taken on the caller's word.** All three matches are re-verified here against
 * Zocdoc and against FHIR at the moment of the write:
 *
 * 1. **time** — the beHuman appointment starts at the same instant as the Zocdoc one;
 * 2. **practitioner** — the Zocdoc provider's mapped practitioner is on that appointment;
 * 3. **patient** — the Zocdoc patient's confirmed chart is on it too, matched by
 *    `ZOCDOC_PATIENT_SYSTEM` rather than by name or birth date.
 *
 * A screen could compute all three and still be wrong: it was rendered minutes ago, and the cost
 * of being wrong is a cancellation landing on a stranger's calendar. So `mismatch` names whichever
 * check failed instead of trusting the click.
 */
export const zocdocLinkAppointment = oc
  .route({ method: 'POST', path: '/v2/zocdoc/appointments/link' })
  .input(
    z.object({
      zocdocAppointmentId: z.string().min(1),
      /** The beHuman `Appointment` id — an id, not a reference. */
      appointmentId: z.string().min(1),
    }),
  )
  .output(
    z.object({
      status: z.enum([
        /** The identifier was written. */
        'linked',
        /** This appointment already carried exactly this Zocdoc id — nothing was written. */
        'already-linked',
        /** Refused: one of the three checks failed. See `mismatch`. */
        'mismatch',
        /** Refused: one side is already bound to something else. See `conflict`. */
        'conflict',
      ]),
      mismatch: z
        .object({
          check: z.enum([
            'time',
            'practitioner',
            'patient',
            'provider-unmapped',
            /** The linked practitioner's chart carries no `us-npi` to query Zocdoc by. */
            'practitioner-has-no-npi',
            /** Zocdoc did not answer for that provider — nothing for an operator to re-map. */
            'zocdoc-unknown',
            'no-chart',
            /**
             * The mapped practitioner belongs to no organization, so there is no practice whose
             * charts this booking could be matched against. Chart lookups are scoped to one
             * practice — a chart belongs to one — and an unscoped search would answer with another
             * practice's record.
             */
            'no-owner-organization',
          ]),
          /** Plain-language detail for the screen; never carries the other patient's name. */
          detail: z.string(),
        })
        .optional(),
      conflict: z
        .object({
          kind: z.enum([
            /** This beHuman appointment is already bound to a different Zocdoc appointment. */
            'appointment-has-other-zocdoc',
            /** This Zocdoc appointment is already bound to a different beHuman appointment. */
            'zocdoc-has-other-appointment',
          ]),
          existing: z.string(),
        })
        .optional(),
      /** The encounter on the bound appointment, which is what a cancellation acts on. */
      encounterId: z.string().optional(),
    }),
  )
